import { FC } from 'react';
import {
  Box,
  Card as MuiCard,
  CardContent as MuiCardContent,
  styled,
  Typography,
  Link,
} from '@mui/material';
import { getQueryKey } from '@trpc/react-query';
import Button from 'components/Button';
import FormField from 'further-ui/components/FormField';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column, Row } from 'further-ui/components/Layout';
import { VehicleApplication } from '../types';
import { CurrencyCode, Vehicle } from 'further-types/lp/vehicle';
import { DocumentCompletionStatus } from 'further-types/lp/investor-signable-document';
import { numberToCurrencyString } from 'further-ui/utils';
import { useDisclosure } from 'further-ui/hooks';
import { SaveAlt } from '@mui/icons-material';
import useFeeTemplateOptions from 'hooks/lp/data/fee-manager/useFeeTemplateOptions';
import useEqualisationInterestFeeTemplateOptions from 'hooks/lp/data/vehicle/useEqualisationInterestFeeTemplateOptions';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import downloadPdf from 'utils/downloadPdf';
import api from 'lib/trpcClient';

const Card = styled(MuiCard)(({ theme }) => ({
  border: `1px solid ${theme.palette.success.main}`,
}));

const CardContent = styled(MuiCardContent)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.lg),
}));

const Document = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(theme.spacingSizes.sm),
  fontSize: theme.fontSizes.regular,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.grey[900],
  lineHeight: '16px',
  backgroundColor: theme.palette.grey[100],
  borderRadius: '8px',
}));

const InputValue = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  fontSize: theme.fontSizes.regular,
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.grey[900],
  lineHeight: '16px',
  backgroundColor: theme.palette.grey[100],
  borderRadius: '8px',
}));

const DeleteButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(theme.spacingSizes.sm),
}));

type Props = {
  title: string;
  application: VehicleApplication;
  vehicle: Vehicle;
  investorLocalCurrency?: CurrencyCode;
  onShowCommitmentForm?: () => void;
};

const CommitmentPreview: FC<Props> = ({
  title,
  application,
  vehicle,
  onShowCommitmentForm,
}) => {
  const vehicleId = vehicle._id;
  const { commitment, investorLocalCurrency, hasClosedCommitment } =
    application;

  const carriedInterestFeeTemplate =
    vehicle.carrySettings.carriedInterestFeeTemplates.find(
      (template) => template._id === commitment?.carriedInterestFeeTemplateId,
    )?.name ?? '--';

  const deleteCommitmentDisclosure = useDisclosure();
  const { mutate: deleteCommitment, isPending } =
    api.commitments.delete.useMutation(
      useHandleTrpcMutation()({
        invalidations: [
          getQueryKey(
            api.onboarding.investorDetails,
            {
              vehicleApplicationId: application.id,
              vehicleId,
            },
            'query',
          ),
        ],
      }),
    );

  const handleDeleteCommitment = () => {
    if (!commitment) {
      return;
    }

    deleteCommitment({
      commitmentId: commitment.id,
      vehicleId,
    });
    deleteCommitmentDisclosure.onClose();
  };

  const { data: applicationDocuments = [] } =
    api.investorSignableDocument.listAllDocumentsForApplication.useQuery({
      vehicleApplicationId: application.id,
      vehicleId,
    });
  const completedApplicationDocuments = applicationDocuments.filter(
    (document) =>
      document.investorSignableDocument?.documentStatus ===
      DocumentCompletionStatus.Completed,
  );

  const { mutate: downloadDocument } =
    api.investorSignableDocument.download.useMutation(
      useHandleTrpcMutation<{ file: string; name: string }>()({
        onSuccess: (data) => {
          downloadPdf(data.file, data.name);
        },
        invalidations: [
          getQueryKey(
            api.investorSignableDocument.listAllDocumentsForApplication,
            {
              vehicleApplicationId: application.id,
              vehicleId,
            },
            'query',
          ),
        ],
      }),
    );

  const handleDownloadDocument = (investorSignableDocumentId?: string) => {
    if (!investorSignableDocumentId) return;

    downloadDocument({
      vehicleId,
      investorSignableDocumentId,
    });
  };

  const lpFeeTemplates = useFeeTemplateOptions(vehicleId, false);
  const equalisationInterestFeeTemplates =
    useEqualisationInterestFeeTemplateOptions(vehicleId);

  const lpFeeTemplate =
    lpFeeTemplates.find(
      (template) => template.value === commitment?.lpFeeTemplateId,
    )?.label ?? '--';

  const equalisationInterestFeeTemplate =
    equalisationInterestFeeTemplates.find(
      (template) =>
        template.value === commitment?.equalisationInterestFeeTemplateId,
    )?.label ?? '--';

  return (
    <Card variant="outlined">
      <CardContent>
        <Row justify="space-between" centered>
          <Typography variant="h5">{title}</Typography>
          <Row justify="space-between" centered>
            {onShowCommitmentForm ? (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={onShowCommitmentForm}
              >
                Edit
              </Button>
            ) : null}
            {!hasClosedCommitment ? (
              <DeleteButton
                variant="contained"
                color="error"
                size="large"
                onClick={deleteCommitmentDisclosure.onOpen}
                loading={isPending}
                disabled={isPending}
              >
                Delete
              </DeleteButton>
            ) : null}
          </Row>
        </Row>
        <Row fullWidth spacing="sm">
          <FormField
            label="Subscription date"
            tooltip="This is the date the investor signed their fund documents and/or subscription agreement. This is not their close date, which is set through the Close & Equalise function."
          >
            <InputValue>
              {commitment?.commitmentDate.toLabel('-', 'MMM dd, yyyy')}
            </InputValue>
          </FormField>
          <FormField label="Total commitment">
            <InputValue>
              {numberToCurrencyString(commitment?.totalCommitmentLcy, {
                currency: vehicle.baseCurrency,
              })}
            </InputValue>
          </FormField>
          <FormField label="Investor local currency">
            <InputValue>{investorLocalCurrency ?? '--'}</InputValue>
          </FormField>
        </Row>
        <Row fullWidth spacing="sm">
          <FormField label="Equalisation interest fee template">
            <InputValue>{equalisationInterestFeeTemplate}</InputValue>
          </FormField>
          <FormField label="Management fee template">
            <InputValue>{lpFeeTemplate}</InputValue>
          </FormField>
          <FormField label="Carried interest template">
            <InputValue>{carriedInterestFeeTemplate}</InputValue>
          </FormField>
        </Row>
        <MuiCard variant="outlined">
          <CardContent>
            <Column fullWidth spacing="sm">
              <Typography variant="h6">Subscription documents</Typography>
              {completedApplicationDocuments.map((document) => (
                <Document key={document.id}>
                  <Box>{document.documentName}</Box>
                  <Box
                    onClick={() => {
                      handleDownloadDocument(
                        document.investorSignableDocument?.id,
                      );
                    }}
                  >
                    <SaveAlt fontSize="small" cursor="pointer" />
                  </Box>
                </Document>
              ))}
              {commitment?.documents.map((document) => (
                <Document key={document.name}>
                  <Box>{document.name}</Box>
                  <Link
                    lineHeight={0}
                    href={document.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <SaveAlt fontSize="small" />
                  </Link>
                </Document>
              ))}
            </Column>
          </CardContent>
        </MuiCard>
      </CardContent>
      <ConfirmDialog
        open={deleteCommitmentDisclosure.isOpen}
        onClose={deleteCommitmentDisclosure.onClose}
        title="Delete commitment"
        content="Are you sure you want to delete this commitment?"
        actions={{
          confirm: {
            label: 'Delete commitment',
            color: 'error',
            onClick: handleDeleteCommitment,
          },
        }}
      />
    </Card>
  );
};

export default CommitmentPreview;
